/*
 * A scroll-snapped row with pagination dots under it.
 *
 * Two places in the theme draw the same thing on mobile: the "How It Works"
 * steps on the Nom Club page and the levels bar of a tier-gated collection.
 * Both lay their items out along a line on desktop, where no JavaScript is
 * needed, and both turn that line into a swipeable row on mobile. The only
 * thing left to do here is keep the dots in step with the scroll position.
 *
 * The dots are decorative (aria-hidden in the markup) — swiping the row is the
 * interaction, they only report where it stopped.
 *
 * Markup contract — the element finds its parts by data attribute, so each
 * caller keeps its own class names:
 *
 *   <snap-pager>
 *     <div data-snap-pager-scroller>
 *       <... data-snap-pager-item> ... </...>   (one per item)
 *     </div>
 *     <div aria-hidden="true">
 *       <span data-snap-pager-page> ... </span> (one per item)
 *     </div>
 *   </snap-pager>
 *
 * Items and pages are expected to come in equal numbers; a page with no item
 * simply never becomes active.
 *
 * An item may also carry `data-snap-pager-start`, and the row opens scrolled to
 * it instead of to the beginning. The levels bar uses that to put the visitor's
 * own tier on screen without a swipe; the steps carousel does not set it and
 * opens at the first step, as before.
 */
(function () {
  if (customElements.get('snap-pager')) return;

  class SnapPager extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('[data-snap-pager-scroller]');
      this.pages = this.querySelectorAll('[data-snap-pager-page]');
      if (!this.scroller || this.pages.length === 0) return;

      this.onScroll = this.onScroll.bind(this);
      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
      this.scrollToStart();
      this.onScroll();
    }

    /* Nothing happens on desktop, where the scroller is an ordinary overflow:
       visible block and scrollLeft cannot move — which is what we want there,
       since the whole row is on screen anyway. */
    scrollToStart() {
      const start = this.querySelector('[data-snap-pager-start]');
      if (!start) return;
      this.scroller.scrollLeft += start.getBoundingClientRect().left - this.scrollportLeft();
    }

    /* Left edge of the scrollport, padding included — an item sitting exactly
       here is the one on show. */
    scrollportLeft() {
      return (
        this.scroller.getBoundingClientRect().left +
        parseFloat(getComputedStyle(this.scroller).paddingLeft)
      );
    }

    disconnectedCallback() {
      if (this.scroller) this.scroller.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.setActivePage();
      });
    }

    setActivePage() {
      const items = this.querySelectorAll('[data-snap-pager-item]');
      if (items.length === 0) return;

      const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
      let active;

      if (maxScroll > 0 && this.scroller.scrollLeft >= maxScroll - 1) {
        // The row is wider than it can scroll, so the last items never reach the
        // left edge — at the end of the scroll the last one is the one on show.
        active = items.length - 1;
      } else {
        // Otherwise it is the item whose left edge sits closest to the left edge
        // of the scrollport.
        const left = this.scrollportLeft();
        let closest = Infinity;
        active = 0;
        items.forEach((item, index) => {
          const distance = Math.abs(item.getBoundingClientRect().left - left);
          if (distance < closest) {
            closest = distance;
            active = index;
          }
        });
      }

      this.pages.forEach((page, index) => {
        page.classList.toggle('is-active', index === active);
      });
    }
  }

  customElements.define('snap-pager', SnapPager);
})();
